import { useEffect, useState, RefObject } from 'react';

const DEFAULT_NARROW = 560;
const DEFAULT_WIDE = 880;

export type GridLayout = 'stacked' | 'double' | 'triple';

/** True when the observed element is narrower than the breakpoint. */
export function useContainerNarrow(
  ref: RefObject<HTMLElement | null>,
  breakpoint = DEFAULT_NARROW,
): boolean {
  const [isNarrow, setIsNarrow] = useState(true);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const update = (width: number) => setIsNarrow(width < breakpoint);
    const ro = new ResizeObserver(([entry]) => update(entry.contentRect.width));
    ro.observe(el);
    update(el.getBoundingClientRect().width);

    return () => ro.disconnect();
  }, [ref, breakpoint]);

  return isNarrow;
}

/** Container-aware grid density for card lists. */
export function useGridLayout(
  ref: RefObject<HTMLElement | null>,
  narrow = DEFAULT_NARROW,
  wide = DEFAULT_WIDE,
): GridLayout {
  const [layout, setLayout] = useState<GridLayout>('stacked');

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const update = (width: number) => {
      if (width < narrow) setLayout('stacked');
      else if (width < wide) setLayout('double');
      else setLayout('triple');
    };
    const ro = new ResizeObserver(([entry]) => update(entry.contentRect.width));
    ro.observe(el);
    update(el.getBoundingClientRect().width);

    return () => ro.disconnect();
  }, [ref, narrow, wide]);

  return layout;
}

export function gridLayoutClass(layout: GridLayout): string {
  if (layout === 'stacked') return 'flex flex-col gap-3';
  if (layout === 'double') return 'grid grid-cols-2 gap-3 sm:gap-4 w-full';
  return 'grid grid-cols-3 gap-3 sm:gap-4 w-full';
}
